<!-- components/ImageWithFallback.vue -->
<template>
  <div v-if="loading" :class="['absolute inset-0 animate-pulse bg-gray-300', className]" :style="style" />
  <div v-if="didError" :class="['inline-block bg-gray-100 text-center align-middle', className]" :style="style">
    <div class="flex items-center justify-center w-full h-full">
      <img
        :src="ERROR_IMG_SRC" alt="Error loading image" v-bind="$attrs" :data-original-url="src"
        class="blur-xl md:blur-2xl saturate-125" @load="onLoad"
      />
    </div>
  </div>
  <img ref="image" v-else :src="src" :alt="alt" :class="[loading ? 'opacity-0' : 'opacity-100', className]" :style="style" v-bind="$attrs" @error="handleError" @load="onLoad" />
</template>

<script setup lang="ts">
import { ref, useTemplateRef, onMounted, watch, nextTick } from 'vue';

const props = defineProps<{
  src?: string;
  alt?: string;
  className?: string;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
  style?: any;
}>();

const imgRef = useTemplateRef<HTMLImageElement>("image")

onMounted(() => nextTick(syncState))

// PhotoGallerySection.vue reuses this same component instance across photos (cellA/cellB
// keep the same position in the template, only their `src` prop changes as pairIndex
// advances) — without this, `loading`/`didError` only ever get set once by the very first
// photo's load/error signal. If that first signal is ever missed (observed in Chrome and
// Safari, not Firefox — likely a load-event race against SSR hydration or the decode()
// preloading in PhotoGallerySection racing the same URL), the cell gets permanently wedged
// at opacity-0 for every photo shown afterwards, since nothing ever re-checks. Resetting on
// every src change gives each photo its own fresh chance to resolve, so the gallery
// self-heals on the very next pair swap instead of staying stuck for the rest of the visit.
watch(() => props.src, () => {
  loading.value = true;
  didError.value = false;
  nextTick(syncState);
})

function syncState() {
  const img = imgRef.value;
  if (!img) return;

  if (img.complete) {
    if (img.naturalWidth > 0) {
      // loaded successfully
      loading.value = false;
      return;
    } else if (img.naturalWidth === 0) {
      // failed
      loading.value = false;
      didError.value = true;
    }
  }
}

const ERROR_IMG_SRC = //'https://placehold.co/600x400?text=No+Image';
'https://images.unsplash.com/photo-1515263487990-61b07816b324?auto=format&fit=crop&w=600&q=40'

const didError = ref(false);
const loading = ref(true);

const handleError = (err: Event) => {
  console.warn(err, (err.target as HTMLImageElement).src)
  didError.value = true;
  loading.value = false;
};

const emit = defineEmits(['load'])

function onLoad(event: Event) {
  emit('load', event)
  loading.value = false
}
</script>
